<template>
  <div>
    <div class="d-flex flex-wrap align-center justify-space-between" style="gap: 12px;">
      <h2 class="mb-0">Cronograma de JOB</h2>
      <v-btn outlined color="primary" @click="$router.push('/relatorios#comercial')">
        <v-icon left>mdi-arrow-left</v-icon>
        Voltar ao menu
      </v-btn>
    </div>

    <div class="d-flex flex-wrap align-center mt-4 mb-4" style="gap: 12px;">
      <v-text-field v-model="filtros.dataInicio" type="date" label="Abertura inicial" solo hide-details clearable class="c-filtro" />
      <v-text-field v-model="filtros.dataFim" type="date" label="Abertura final" solo hide-details clearable class="c-filtro" />
      <v-autocomplete
        v-model="filtros.job"
        :items="opcoesJobs"
        label="JOB"
        solo
        hide-details
        clearable
        class="c-filtro"
      />
      <v-autocomplete
        v-model="filtros.cliente"
        :items="opcoesClientes"
        label="Cliente"
        solo
        hide-details
        clearable
        class="c-filtro"
      />
      <v-autocomplete
        v-model="filtros.coordenador"
        :items="opcoesCoordenadores"
        label="Coordenador"
        solo
        hide-details
        clearable
        class="c-filtro"
      />

      <v-btn color="primary" :loading="loading" @click="buscarDados">
        <v-icon left>mdi-refresh</v-icon>
        Recarregar
      </v-btn>

      <v-menu>
        <template v-slot:activator="{ on }">
          <v-btn color="primary" outlined v-on="on" :disabled="loading || dadosFiltrados.length === 0">
            <v-icon left>mdi-file-export-outline</v-icon>
            Exportar
          </v-btn>
        </template>
        <v-list>
          <v-list-item link @click="exportarXlsx"><v-list-item-title>Excel</v-list-item-title></v-list-item>
          <v-list-item link @click="exportarPdfCronograma"><v-list-item-title>PDF</v-list-item-title></v-list-item>
        </v-list>
      </v-menu>
    </div>

    <div ref="areaPdf">
      <v-data-table
        :headers="headers"
        :items="dadosFiltrados"
        :loading="loading"
        item-key="_id"
        rows-per-page-text="Linhas por página:"
        page-text="{0}-{1} de {2}"
        class="elevation-1"
      >
        <template v-slot:item.dataAbertura="{ item }">{{ formatarDataPtBr(item.dataAbertura) }}</template>
        <template v-slot:item.dataEncerramento="{ item }">{{ formatarDataPtBr(item.dataEncerramento) }}</template>
        <template v-slot:item.valorJob="{ item }">{{ formatarMoedaPtBr(item.valorJob) }}</template>
        <template v-slot:item.saldoAReceber="{ item }">{{ formatarMoedaPtBr(item.saldoAReceber) }}</template>
      </v-data-table>
    </div>
  </div>
</template>

<script>
import relatorioJobService from "@/views/Relatorios/services/relatorioJobService";
import relatorioPermissionMixin from "@/mixins/relatorioPermissionMixin";
import UtilCronogramaJobPDF from "@/utils/UtilCronogramaJobPDF";
import * as XLSX from "xlsx";
import {
  formatarDataPtBr,
  formatarMoedaPtBr,
  normalizarTexto
} from "@/views/Relatorios/base/relatorioBaseUtils";

export default {
  name: "RelatorioCronogramaJobView",
  mixins: [relatorioPermissionMixin],
  data() {
    return {
      relatorioSchemas: ["Project"],
      relatorioFiltrarPorOwner: true,
      loading: false,
      dados: [],
      expandidos: [],
      filtros: {
        dataInicio: "",
        dataFim: "",
        job: "",
        cliente: "",
        coordenador: ""
      },
      headers: [
        { text: "JOB", value: "code" },
        { text: "Abertura", value: "dataAbertura" },
        { text: "Encerramento", value: "dataEncerramento" },
        { text: "Cliente", value: "clienteNome" },
        { text: "Descrição", value: "description" },
        { text: "Condições de Pagamento", value: "condicaoDePagamento" },
        { text: "Coordenador", value: "coordenadorNome" },
        { text: "Valor Job", value: "valorJob" },
        { text: "Saldo a Receber", value: "saldoAReceber" }
      ]
    };
  },
  computed: {
    opcoesJobs() {
      return [...new Set(this.dados.map((item) => normalizarTexto(item.code)).filter(Boolean))].sort((a, b) => a.localeCompare(b));
    },
    opcoesClientes() {
      return [...new Set(this.dados.map((item) => normalizarTexto(item.clienteNome)).filter(Boolean))].sort((a, b) => a.localeCompare(b));
    },
    opcoesCoordenadores() {
      return [...new Set(this.dados.map((item) => normalizarTexto(item.coordenadorNome || item.vendedorCoordenador)).filter(Boolean))].sort((a, b) => a.localeCompare(b));
    },
    dadosFiltrados() {
      const job = normalizarTexto(this.filtros.job).toLowerCase();
      const cliente = normalizarTexto(this.filtros.cliente).toLowerCase();
      const coordenador = normalizarTexto(this.filtros.coordenador).toLowerCase();

      return this.dados.filter((item) => {
        const codeOk = !job || normalizarTexto(item.code).toLowerCase().includes(job);
        const clienteOk = !cliente || normalizarTexto(item.clienteNome).toLowerCase().includes(cliente);
        const coordenadorOk = !coordenador || normalizarTexto(item.coordenadorNome || item.vendedorCoordenador).toLowerCase().includes(coordenador);
        return codeOk && clienteOk && coordenadorOk;
      });
    }
  },
  watch: {
    "filtros.dataInicio"() { this.buscarDados(); },
    "filtros.dataFim"() { this.buscarDados(); }
  },
  methods: {
    formatarDataPtBr,
    formatarMoedaPtBr,
    async buscarDados() {
      this.loading = true;
      try {
        const lista = await relatorioJobService.listarRelatorioJob(this.filtros);
        this.dados = Array.isArray(lista) ? lista : [];
        this.expandidos = [];
      } finally {
        this.loading = false;
      }
    },
    async exportarXlsx() {
      this.loading = true;
      try {
        const rows = await UtilCronogramaJobPDF.gerarLinhasExcel(this.dadosFiltrados);
        const ws = XLSX.utils.aoa_to_sheet(rows);
        ws["!cols"] = [
          { wch: 12 }, { wch: 28 }, { wch: 14 }, { wch: 55 }, { wch: 12 },
          { wch: 12 }, { wch: 12 },
          { wch: 12 }, { wch: 12 },
          { wch: 12 }, { wch: 12 },
          { wch: 12 }, { wch: 12 },
          { wch: 12 }, { wch: 12 },
          { wch: 14 }, { wch: 14 },
          { wch: 35 }, { wch: 5 }, { wch: 5 },
          { wch: 14 }, { wch: 14 }
        ];
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Cronograma JOB");
        const hoje = new Date().toISOString().slice(0, 10);
        XLSX.writeFile(wb, `cronograma-job_${hoje}.xlsx`);
      } finally {
        this.loading = false;
      }
    },
    async exportarPdfCronograma() {
      this.loading = true;
      try {
        await UtilCronogramaJobPDF.gerarCronograma(this.dadosFiltrados);
      } finally {
        this.loading = false;
      }
    }
  },
  async mounted() {
    if (this.relatorioTemPermissao(this.relatorioSchemas)) {
      await this.buscarDados();
    }
  }
};
</script>

<style scoped>
.c-filtro {
  max-width: 200px;
}
</style>
